/**
 * @file CRLRecommendationCard.jsx
 * @module ui/src/components/tenant-admin/CRLRecommendationCard
 * @description
 *   Presents the Comp Role Library recommendation associated with a plan and
 *   requires explicit acknowledgement when the authored design meaningfully
 *   departs from that recommendation.
 *
 * @version 1.0.0
 * @since S755
 * @author data-fit SME Build · Reviewed: Claude S755
 *
 * @architecture
 *   Locked decisions : #13
 *   Temporal invariants : none
 *   Design principles : DP-5, DP-10, DP-14
 *   Cross-cutting contracts : XC-7
 *
 * @domain-rules
 *   - CRL presents one server-supplied recommendation for the role, segment, and spine context.
 *   - Divergence is determined by recommendation.hasDivergence from the server and is never recomputed from currentConfig.
 *   - Departure from the CRL recommendation requires an explicit "Keep my configuration" acknowledgement.
 *   - An acknowledged departure is represented by designSource = 'crl_override'.
 *   - Restoring the recommendation is a separate explicit action.
 *   - Governance mutations are not reflected optimistically; receipt state changes only when refreshed server data says so.
 *   - spineCode is an internal identifier and is never rendered.
 *   - State is always named in text; color is supplementary only.
 *   - readOnly controls action availability and does not represent persona or temporal applicability.
 *
 * @session-6-migration-flags
 *   - none
 *
 * @changelog
 *   1.0.0  S755  Initial build — Group C batch delivery
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {Object} CRLRecommendation
 * @property {string} roleLabel
 * @property {string} segmentLabel
 * @property {string} spineLabel
 * @property {string} spineCode
 * @property {number} baseVariableSplit
 * @property {string[]} recommendedModifiers
 * @property {'crl'|'crl_override'|'custom'} designSource
 * @property {boolean} hasDivergence
 * @property {string[]} divergencePoints
 * @property {string} [rationale]
 */

/**
 * @typedef {Object} CRLRecommendationCardProps
 * @property {CRLRecommendation} recommendation
 * @property {Object} currentConfig
 * @property {boolean} readOnly
 * @property {Function} onApply
 * @property {Function} onDismiss
 */

const styles = {
  card: {
    border: '1px solid rgb(var(--color-border))',
    borderRadius: 8,
    background: 'rgb(var(--color-surface))',
    padding: 16,
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    color: 'rgb(var(--color-text))',
  },
  divergentCard: {
    border: '1px solid rgb(var(--color-warning))',
    borderRadius: 8,
    background: 'rgb(var(--color-surface))',
    padding: 16,
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    color: 'rgb(var(--color-text))',
  },
  headingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  heading: {
    margin: 0,
    fontSize: 'var(--font-sz-base)',
    fontWeight: 600,
  },
  stateLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: 999,
    padding: '2px 8px',
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  muted: {
    color: 'rgb(var(--color-text-muted))',
    margin: '6px 0 0',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
    marginTop: 14,
  },
  label: {
    display: 'block',
    color: 'rgb(var(--color-text-muted))',
    fontSize: 12,
    marginBottom: 2,
  },
  value: {
    margin: 0,
  },
  list: {
    margin: '8px 0 0',
    paddingLeft: 20,
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 14,
  },
  rationale: {
    marginTop: 14,
    padding: 12,
    borderRadius: 6,
    background: 'rgb(var(--color-surface-muted))',
  },
};

/**
 * Format the recommendation's base-variable split strictly from the
 * server-supplied recommendation value.
 *
 * WHY: This formats an already-defined CRL recommendation for display; it does
 * not infer plan economics from currentConfig or any raw configuration payload.
 *
 * @param {number} baseVariableSplit
 * @returns {string}
 */
function formatBaseVariableSplit(baseVariableSplit) {
  if (!Number.isFinite(baseVariableSplit)) {
    return 'Not specified';
  }

  const base = Math.round(baseVariableSplit * 100);
  return `${base}/${100 - base}`;
}

/**
 * @param {CRLRecommendationCardProps} props
 * @returns {JSX.Element}
 */
export default function CRLRecommendationCard({
  recommendation,
  currentConfig,
  readOnly,
  onApply,
  onDismiss,
}) {
  // WHY: Divergence detail is progressively disclosed rather than forced into
  // a blocking modal. The distinction remains available without overwhelming
  // the normal plan-authoring path (DP-5).
  const [acknowledgeExpanded, setAcknowledgeExpanded] = useState(false);

  // WHY: CRL acknowledgement and restoration may result in governed server-side
  // changes. We prevent duplicate requests while awaiting confirmed outcomes
  // and never rewrite recommendation state optimistically (DP-10).
  const [pendingAction, setPendingAction] = useState(null);

  // WHY: Mutation failures must remain visible to the administrator because a
  // failed acknowledgement must never look like a recorded override.
  const [actionError, setActionError] = useState(null);

  // WHY: currentConfig is part of the parent/document contract, but this
  // component deliberately does not inspect it. Divergence is server-owned
  // through recommendation.hasDivergence under locked decision #13.
  void currentConfig;

  const {
    roleLabel,
    segmentLabel,
    spineLabel,
    baseVariableSplit,
    recommendedModifiers,
    designSource,
    hasDivergence,
    divergencePoints,
    rationale,
  } = recommendation;

  /**
   * WHY: The callback is awaited so the UI does not claim that a departure was
   * recorded until the parent/server completes successfully. A refreshed
   * designSource='crl_override' payload remains the source of receipt state.
   */
  const handleKeepConfiguration = async () => {
    setPendingAction('dismiss');
    setActionError(null);

    try {
      await onDismiss();
    } catch (error) {
      setActionError(
        error?.message ||
          'The departure acknowledgement could not be recorded. Your plan has not been changed.',
      );
    } finally {
      setPendingAction(null);
    }
  };

  /**
   * WHY: Restoring the recommendation is also governed as an explicit action.
   * No local recommendation fields are changed before the server confirms.
   */
  const handleRestoreRecommendation = async () => {
    setPendingAction('apply');
    setActionError(null);

    try {
      await onApply();
    } catch (error) {
      setActionError(
        error?.message ||
          'The CRL recommendation could not be restored. Your plan has not been changed.',
      );
    } finally {
      setPendingAction(null);
    }
  };

  // WHY: Three render branches are kept strictly separate because the domain
  // language of each is meaningfully different.
  //
  // Branch 1 — crl_override: the admin explicitly acknowledged a departure.
  //   Render the durable receipt that names the departure. This is correct
  //   regardless of readOnly because the departure is a recorded governance fact.
  //
  // Branch 2 — readOnly (plan approved, no override recorded): show the
  //   informational state without any departure language and without action
  //   buttons. The admin did not "choose to keep" anything — the plan is
  //   simply approved.
  //
  // Branch 3 — editable, no divergence: CRL informational state, no action needed.
  //
  // Branch 4 — editable, divergence present: full acknowledgement card with actions.
  //
  // Conflating Branch 1 and Branch 2 into one condition (|| readOnly) produces
  // incorrect domain language on approved plans with no recorded departure.
  // This is corrected here (OI-S755-C20-READONLYBRANCH-01).

  // ── BRANCH 1: Departure has been explicitly recorded ────────────────────────
  if (designSource === 'crl_override') {
    return (
      <section
        style={styles.card}
        aria-label="Comp Role Library recommendation"
        aria-live="polite"
      >
        <div style={styles.headingRow}>
          <div>
            <h3 style={styles.heading}>CRL recommendation</h3>
            <p style={styles.muted}>
              Recommendation for {roleLabel} / {segmentLabel}
            </p>
          </div>
          <span style={styles.stateLabel}>Departure acknowledged</span>
        </div>

        <p>
          You chose to keep your configuration, which differs from the CRL
          recommendation for {roleLabel}.
        </p>

        {divergencePoints.length > 0 && (
          <ul style={styles.list}>
            {divergencePoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        )}

        <div style={styles.summaryGrid}>
          <div>
            <span style={styles.label}>Recommended earning model</span>
            <p style={styles.value}>{spineLabel}</p>
          </div>
          <div>
            <span style={styles.label}>Recommended base / variable split</span>
            <p style={styles.value}>
              {formatBaseVariableSplit(baseVariableSplit)}
            </p>
          </div>
        </div>

        {recommendedModifiers.length > 0 && (
          <div>
            <span style={{ ...styles.label, marginTop: 14 }}>
              Recommended modifiers
            </span>
            <ul style={styles.list}>
              {recommendedModifiers.map((modifier) => (
                <li key={modifier}>{modifier}</li>
              ))}
            </ul>
          </div>
        )}

        {/* WHY: rationale is CA-facing governance evidence. It is labeled
            explicitly so it cannot be mistaken for rep-facing plan copy.
            Guard is correct — rationale is optional
            (OI-S755-C20-RATIONALE-NULLGUARD-01). */}
        {rationale && (
          <div style={styles.rationale}>
            <strong>Design rationale (CA use only)</strong>
            <p style={{ marginBottom: 0 }}>{rationale}</p>
          </div>
        )}
      </section>
    );
  }

  // ── BRANCH 2: Plan is approved/read-only, no departure recorded ─────────────
  // WHY: readOnly means the plan is approved. The admin did not explicitly choose
  // to keep any configuration — the plan simply went through normal governance.
  // We show an informational summary without any departure language and without
  // action buttons. This is intentionally different from Branch 1. (INV-3 —
  // readOnly is not a temporal signal; it is an editability contract only.)
  if (readOnly) {
    return (
      <section
        style={styles.card}
        aria-label="Comp Role Library recommendation"
      >
        <div style={styles.headingRow}>
          <div>
            <h3 style={styles.heading}>CRL recommendation</h3>
            <p style={styles.muted}>
              Recommendation for {roleLabel} / {segmentLabel}
            </p>
          </div>
          <span style={styles.stateLabel}>
            {hasDivergence
              ? 'Differs from recommendation'
              : 'Follows recommendation'}
          </span>
        </div>

        {hasDivergence ? (
          <>
            <p>
              This approved plan differs from the CRL recommendation for{' '}
              {roleLabel} in the following areas:
            </p>
            {divergencePoints.length > 0 && (
              <ul style={styles.list}>
                {divergencePoints.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <p>
            This plan follows the recommended configuration for {roleLabel} /{' '}
            {segmentLabel}.
          </p>
        )}

        <div style={styles.summaryGrid}>
          <div>
            <span style={styles.label}>Recommended earning model</span>
            <p style={styles.value}>{spineLabel}</p>
          </div>
          <div>
            <span style={styles.label}>Recommended base / variable split</span>
            <p style={styles.value}>
              {formatBaseVariableSplit(baseVariableSplit)}
            </p>
          </div>
        </div>

        {recommendedModifiers.length > 0 && (
          <div>
            <span style={{ ...styles.label, marginTop: 14 }}>
              Recommended modifiers
            </span>
            <ul style={styles.list}>
              {recommendedModifiers.map((modifier) => (
                <li key={modifier}>{modifier}</li>
              ))}
            </ul>
          </div>
        )}

        {rationale && (
          <div style={styles.rationale}>
            <strong>Design rationale (CA use only)</strong>
            <p style={{ marginBottom: 0 }}>{rationale}</p>
          </div>
        )}

        {/* WHY: No action buttons are rendered here because this is the
            approved/read-only presentation state. Governance mutations are
            unavailable from this branch. */}
      </section>
    );
  }

  // ── BRANCH 3: Editable, no server-declared divergence ───────────────────────
  // WHY: When hasDivergence is false the CRL is informational only.
  // No acknowledgement is required to continue authoring (locked decision #13).
  if (!hasDivergence) {
    return (
      <section
        style={styles.card}
        aria-label="Comp Role Library recommendation"
      >
        <div style={styles.headingRow}>
          <div>
            <h3 style={styles.heading}>CRL recommendation</h3>
            <p style={styles.muted}>
              This plan follows the recommended configuration for {roleLabel} /{' '}
              {segmentLabel}
            </p>
          </div>
          <span style={styles.stateLabel}>Follows recommendation</span>
        </div>

        <div style={styles.summaryGrid}>
          <div>
            <span style={styles.label}>Recommended earning model</span>
            <p style={styles.value}>{spineLabel}</p>
          </div>
          <div>
            <span style={styles.label}>Recommended base / variable split</span>
            <p style={styles.value}>
              {formatBaseVariableSplit(baseVariableSplit)}
            </p>
          </div>
        </div>

        {recommendedModifiers.length > 0 && (
          <div>
            <span style={{ ...styles.label, marginTop: 14 }}>
              Recommended modifiers
            </span>
            <ul style={styles.list}>
              {recommendedModifiers.map((modifier) => (
                <li key={modifier}>{modifier}</li>
              ))}
            </ul>
          </div>
        )}

        {rationale && (
          <div style={styles.rationale}>
            <strong>Design rationale (CA use only)</strong>
            <p style={{ marginBottom: 0 }}>{rationale}</p>
          </div>
        )}
      </section>
    );
  }

  // ── BRANCH 4: Editable, divergence declared by server ───────────────────────
  // WHY: A meaningful departure must be explicitly acknowledged. Leaving the
  // page or ignoring the prompt never records crl_override (locked decision #13).
  return (
    <section
      style={styles.divergentCard}
      aria-label="CRL recommendation departure"
    >
      <div style={styles.headingRow}>
        <div>
          <h3 style={styles.heading}>CRL recommendation</h3>
          <p style={styles.muted}>
            This differs from the recommended configuration for {roleLabel} /{' '}
            {segmentLabel}.
          </p>
        </div>
        <span style={styles.stateLabel}>Differs from recommendation</span>
      </div>

      <button
        type="button"
        className="text-button"
        aria-expanded={acknowledgeExpanded}
        aria-controls="crl-divergence-details"
        onClick={() => setAcknowledgeExpanded((expanded) => !expanded)}
      >
        {acknowledgeExpanded
          ? 'Hide recommendation details'
          : 'Review recommendation details'}
      </button>

      {acknowledgeExpanded && (
        <div id="crl-divergence-details">
          <div style={styles.summaryGrid}>
            <div>
              <span style={styles.label}>Recommended earning model</span>
              <p style={styles.value}>{spineLabel}</p>
            </div>
            <div>
              <span style={styles.label}>Recommended base / variable split</span>
              <p style={styles.value}>
                {formatBaseVariableSplit(baseVariableSplit)}
              </p>
            </div>
          </div>

          <div>
            <span style={{ ...styles.label, marginTop: 14 }}>
              What differs
            </span>
            <ul style={styles.list}>
              {divergencePoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>

          {recommendedModifiers.length > 0 && (
            <div>
              <span style={{ ...styles.label, marginTop: 14 }}>
                Recommended modifiers
              </span>
              <ul style={styles.list}>
                {recommendedModifiers.map((modifier) => (
                  <li key={modifier}>{modifier}</li>
                ))}
              </ul>
            </div>
          )}

          {rationale && (
            <div style={styles.rationale}>
              <strong>Design rationale (CA use only)</strong>
              <p style={{ marginBottom: 0 }}>{rationale}</p>
            </div>
          )}
        </div>
      )}

      {actionError && (
        <div
          className="modifier-manager-inline-error"
          role="alert"
          style={{ marginTop: 12 }}
        >
          {actionError}
        </div>
      )}

      <div style={styles.actionRow}>
        <button
          type="button"
          className="secondary-button"
          disabled={pendingAction !== null}
          onClick={handleKeepConfiguration}
        >
          {pendingAction === 'dismiss'
            ? 'Recording choice…'
            : 'Keep my configuration'}
        </button>

        <button
          type="button"
          className="primary-button"
          disabled={pendingAction !== null}
          onClick={handleRestoreRecommendation}
        >
          {pendingAction === 'apply'
            ? 'Restoring…'
            : 'Restore recommendation'}
        </button>
      </div>
    </section>
  );
}

CRLRecommendationCard.propTypes = {
  recommendation: PropTypes.shape({
    roleLabel: PropTypes.string.isRequired,
    segmentLabel: PropTypes.string.isRequired,
    spineLabel: PropTypes.string.isRequired,
    spineCode: PropTypes.string.isRequired,
    baseVariableSplit: PropTypes.number.isRequired,
    recommendedModifiers: PropTypes.arrayOf(PropTypes.string).isRequired,
    designSource: PropTypes.oneOf(['crl', 'crl_override', 'custom']).isRequired,
    hasDivergence: PropTypes.bool.isRequired,
    divergencePoints: PropTypes.arrayOf(PropTypes.string).isRequired,
    rationale: PropTypes.string,
  }).isRequired,
  currentConfig: PropTypes.object.isRequired,
  readOnly: PropTypes.bool.isRequired,
  onApply: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

/**
 * @example
 * <CRLRecommendationCard
 *   recommendation={{
 *     roleLabel: 'Enterprise Account Executive',
 *     segmentLabel: 'Enterprise',
 *     spineLabel: 'Quota attainment',
 *     spineCode: 'ARR_QUOTA',
 *     baseVariableSplit: 0.6,
 *     recommendedModifiers: ['Revenue Type', 'Multi-Year'],
 *     designSource: 'custom',
 *     hasDivergence: true,
 *     divergencePoints: ['Base / variable split differs from recommendation'],
 *     rationale: 'Enterprise new-logo roles typically use a balanced base and variable mix.',
 *   }}
 *   currentConfig={planConfig}
 *   readOnly={false}
 *   onApply={() => restoreCRLRecommendation()}
 *   onDismiss={() => acknowledgeCRLDeparture()}
 * />
 */

/*
 * DOMAIN COMPLIANCE CHECK
 * - Locked decision #13: CRL departure is recorded only through explicit action.
 * - recommendation.hasDivergence is trusted as server-computed; currentConfig is never inspected.
 * - designSource='crl_override' produces a durable acknowledgement receipt.
 * - readOnly without crl_override produces informational approved-plan language only and never claims an acknowledgement occurred.
 * - spineCode is accepted for contract completeness but never rendered.
 * - DP-5: recommendation details are progressively disclosed.
 * - DP-10: no optimistic mutation of designSource or divergence state.
 * - DP-14 / XC-7: all states have visible textual names.
 * - readOnly suppresses all departure/restoration actions.
 * - OI-S755-C20-READONLYBRANCH-01: closed by strict four-branch rendering.
 * - OI-S755-C20-RATIONALE-NULLGUARD-01: closed; rationale is optional and guarded before rendering.
 */

/*
 * SESSION 6 MIGRATION FLAGS
 * - none
 */

/*
 * ACCESSIBILITY NOTES
 * - Divergence details use aria-expanded and aria-controls.
 * - The recorded departure acknowledgement receipt uses aria-live="polite".
 * - Divergence points are semantic <ul>/<li> content.
 * - Both governance actions use explicit visible labels; neither is icon-only.
 * - Read-only branches expose no action controls.
 * - Mutation failures use role="alert".
 * - State is always conveyed with visible text and never by color alone.
 */